import { useState, useCallback } from "react";
import { Modal, Text, Checkbox, BlockStack } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";

interface DuplicateEventModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (options: {
    copyTickets: boolean;
    copyAgenda: boolean;
    copyGallery: boolean;
  }) => void;
  loading: boolean;
}

export default function DuplicateEventModal({
  open,
  onClose,
  onConfirm,
  loading,
}: DuplicateEventModalProps) {
  const { t } = useTranslation();
  const [copyTickets, setCopyTickets] = useState(false);
  const [copyGallery, setCopyGallery] = useState(false);

  const handleConfirm = useCallback(() => {
    onConfirm({ copyTickets, copyAgenda: true, copyGallery });
  }, [copyTickets, copyGallery, onConfirm]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setCopyTickets(false);
      setCopyGallery(false);
      onClose();
    }
  }, [loading, onClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("eventDetails.duplicate.modalTitle")}
      primaryAction={{
        content: t("eventDetails.duplicate.confirm"),
        onAction: handleConfirm,
        loading: loading,
        disabled: loading,
      }}
      secondaryActions={[
        {
          content: t("eventDetails.duplicate.cancel"),
          onAction: handleClose,
          disabled: loading,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p" variant="bodyMd">
            {t("eventDetails.duplicate.confirmMessage")}
          </Text>
          <Checkbox
            label={t("eventDetails.duplicate.copyTickets")}
            checked={copyTickets}
            onChange={setCopyTickets}
            disabled={loading}
          />
          <Checkbox
            label={t("eventDetails.duplicate.copyGallery")}
            checked={copyGallery}
            onChange={setCopyGallery}
            disabled={loading}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

